import { useEffect, useRef, useState } from "react";
import { Button, Icon, ButtonContent, Label, Message, Popup } from "semantic-ui-react";
import { AddMemberModal } from "./AddMember";
import { LoadingWrapper, ReactTable } from "../common";
import { Link } from "react-router-dom";
import { canEditAll, fetchWithAuth, filterDataByTeam, getCSRFToken, isReadOnly } from "../auth";
import { statusMap } from "../common/helpers";
import { formatComplianceStatus } from "../../constants/memberProfile";
import "../../css/members.css"


export const fieldToNames = {
  'userId': 'User ID',
  'name': 'Name',
  'supervisor': 'Supervisor',
  'jobTitle': 'Job Title',
  'role': 'Role',
  'status': 'Status',
  'sick': 'Sick',
  'pto': 'PTO',
  'training': 'Training',
  'active': 'Active',
  'unavailable': 'Unavailable'
}

export const MemberStatuses = [
  {
    value: "pto",
    key: "pto",
    text: "PTO"
  },
  {
    value: "training",
    key: "training",
    text: "Training"
  },
  {
    value: "active",
    key: "active",
    text: "Active"
  },
  {
    value: "inactive",
    key: "inactive",
    text: "Inactive"
  },
  {
    value: "sick",
    key: "sick",
    text: "Sick"
  },
  {
    value: "unavailable",
    key: "unavailable",
    text: "Unavailable"
  },
    {
    value: "teamevent",
    key: "teamevent",
    text: "Team Event"
  }
]

export const Members = (props) => {
  const [roleOptions, setRoleOptions] = useState([])
  const [columnInfo, setColumnInfo] = useState([])
  const [members, setMembers] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const selectedRef = useRef({})

  const exportMembers = async () => {
    setLoaded(false)
    setError("")
    const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'membersexport/', {
      headers: { 'X-CSRFToken': getCSRFToken() }
    })
    if(!resp.ok) {
      setError("Something went wrong when exporting members.")
    }
    const respBlob = await resp.blob()
    const url = window.URL.createObjectURL(respBlob)
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Members.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setLoaded(true)
  }

  async function getMembers() {
    setLoaded(false)
    const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/', {
      headers: { 'X-CSRFToken': getCSRFToken() },
      credentials: 'include'
    })
    const respJson = await resp.json()
    let user_info = filterDataByTeam()
    if (!isReadOnly() && user_info && user_info.filterData) {
      let associatedTeamIds = user_info.associatedTeamsIds
      let filtered_members_data = respJson.filter(function (o) {
        return (associatedTeamIds && o.teamIds && associatedTeamIds.includes(o.teamIds[0]));
      });
      setMembers(filtered_members_data)
    } else {
      setMembers(respJson)
    }

    setLoaded(true)
  }

  async function deleteSelectedMembers() {
    async function deleteMembersPost(memberId) {
      await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/' + memberId + '/',
        {
          method: "DELETE",
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      )
    }
    for (const memberIndex in selectedRef.current) {
      try {
        const toDelete = selectedRef.current[memberIndex]
        if (toDelete) {
          const memberId = members[memberIndex].userId
          await deleteMembersPost(memberId)
        }
      } catch { }  // some deleted member from earlier still in the state
    }
    selectedRef.current = {}
    getMembers()
  }

  async function addSelectedToLimitedEditGroup() {
    setError('')
    setSuccess('')
    let errored = []
    async function addToLimitedEditGroupPost(memberId, memberName) {
      const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/' + memberId + '/elevate/',
        {
          method: "POST",
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      )
      const data = await resp.json()
      if (resp.status === 200) {
        setSuccess(data.message)
      }
      else if (resp.status === 401) {
        setError('The username or password is incorrect.')
      }
      else if (resp.status === 403) {
        setError('User does not have permission for this operation.')
      } else if (resp.status === 500) {
        errored.push(data.message + memberName)
      }
    }
    for (const memberIndex in selectedRef.current) {
      try {
        const toAdd = selectedRef.current[memberIndex]
        if (toAdd) {
          const memberId = members[memberIndex].userId
          const memberName = members[memberIndex].name
          await addToLimitedEditGroupPost(memberId, memberName)
        }
      } catch { }  // some deleted member from earlier still in the state
    }
    if (errored.length > 0) {
      setError("Failed to add to LimitedGroup: " + errored)
    }
    selectedRef.current = {}
    getMembers()
  }

  function addToEditGroup() {
    addSelectedToLimitedEditGroup()
  }

  async function removeSelectedFromLimitedEditGroup() {
    setError('')
    setSuccess('')
    let errored = []
    async function removeFromLimitedEditGroupPost(memberId, memberName) {
      const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'members/' + memberId + '/revoke/',
        {
          method: "POST",
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      )
      const data = await resp.json()
      if (resp.status === 200) {
        setSuccess(data.message)
      }
      else if (resp.status === 401) {
        setError('The username or password is incorrect.')
      }
      else if (resp.status === 403) {
        setError('User does not have permission for this operation.')
      } else if (resp.status === 500) {
        errored.push(data.message + memberName)
      }
    }
    for (const memberIndex in selectedRef.current) {
      try {
        const toRemove = selectedRef.current[memberIndex]
        if (toRemove) {
          const memberId = members[memberIndex].userId
          const memberName = members[memberIndex].name
          await removeFromLimitedEditGroupPost(memberId, memberName)
        }
      } catch { }  // some deleted member from earlier still in the state
    }
    if (errored.length > 0) {
      setError("Failed to removeFromLimitedGroup: " + errored)
    }
    selectedRef.current = {}
    getMembers()
  }

  function removeFromEditGroup() {
    removeSelectedFromLimitedEditGroup()
  }

  useEffect(() => {
    getMembers()
  }, [])

  function updateSelected(ev) {
    selectedRef.current = { ...selectedRef.current, ...ev }
  }

  useEffect(() => {
    async function getRoles() {
      const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'roles/', {
        headers: { 'X-CSRFToken': getCSRFToken() },
        credentials: 'include'
      })
      const respJson = await resp.json()
      const newRoleOptions = respJson.map((role) => {
        return { 'text': role.value, 'value': role.key }
      })
      setRoleOptions(newRoleOptions)
    }
    getRoles()
  }, [])

  useEffect((isEdit) => {
    setColumnInfo([
      {
        Header: "ID",
        accessor: "userId"
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: (row) => {
          return (
            <Link to={"/members/" + row.row.original.userId + "/view"}>
              {row.row.original.name}
            </Link>
          )
        }
      },
      {
        Header: "Role",
        accessor: "role",
        Cell: (row) => {
          return <div>
            {row.row.original.role}
            {
              (row.row.original.editPermissions === 'Elevated Associate')
                ? <span> <Label color={'blue'}>Can Edit</Label></span>
                : ''
            }
          </div>
        }

      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (row) => {
          return <div>
            {statusMap[row.row.original.status] ? statusMap[row.row.original.status] : row.row.original.status}
          </div>
        }
      },
      {
        Header: "Team",
        accessor: "teamNames",
      },
      {
        Header: "CDL Status",
        id: "cdlStatus",
        accessor: (row) => formatComplianceStatus(row.cdlStatus),
      },
      {
        Header: "DOT Status",
        id: "dotStatus",
        accessor: (row) => formatComplianceStatus(row.dotStatus),
      },
    ])
  }, [roleOptions])

  return (
    <div>
      {error !== '' &&
        <Message negative>{error}</Message>
      }
      {success !== '' &&
        <Message positive>{success}</Message>
      }
      <LoadingWrapper loading={!loaded} text="Loading members">
        <ReactTable
          className="channel-table"
          data={members}
          columns={columnInfo}
          hiddenColumns={['userId']}
          editable={canEditAll()}
          setEdit={updateSelected}
        />
        {canEditAll() && <div style={{ width: "100%" }}>
          <div>
            <AddMemberModal refresh={getMembers} members={members} />
            <Popup on='click' trigger={<Button style={{ backgroundColor: "var(--tds-dark-blue)", color: "white" }}>Edit selected</Button>} >
              <div style={{ width: "20rem", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <Button style={{ width: "100%", margin: "0.5rem", color: "white", backgroundColor: "var(--delete)" }} onClick={deleteSelectedMembers} animated='vertical'>
                  <ButtonContent visible>Delete selected</ButtonContent>
                  <ButtonContent hidden>Confirm</ButtonContent>
                </Button>
                <Button style={{ width: "100%", margin: "0.5rem", color: "white", backgroundColor: "var(--tds-dark-blue)" }} color="blue" onClick={addToEditGroup} animated='vertical'>
                  <ButtonContent visible>Add To Limited Edit Group</ButtonContent>
                  <ButtonContent hidden>Confirm</ButtonContent>
                </Button>
                <Button style={{ width: "100%", margin: "0.5rem" }} color="orange" onClick={removeFromEditGroup} animated='vertical'>
                  <ButtonContent visible>Remove From Limited Edit Group</ButtonContent>
                  <ButtonContent hidden>Confirm</ButtonContent>
                </Button>
              </div>
            </Popup>
          </div>
        </div>}
        <Button onClick={exportMembers}><Icon className="download" />Export</Button>
      </LoadingWrapper>
    </div>
  );
}
